import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * GrowthKit — Centralized collection of ready-to-paste marketing drafts.
 * All content is pre-written in Italian with authentic founder voice.
 * Each card has: platform, title, body (markdown), copy button, "where to post" link.
 *
 * Why not fully automated? Posting to LinkedIn/Reddit/Facebook groups from a
 * backend violates platform ToS and risks account suspension. The next-best
 * thing is 1-click copy + guided manual posting from Alfio's own device.
 */

const DRAFTS = [
  // -------- LinkedIn 3 settimane di contenuto --------
  {
    id: "linkedin-week-1",
    platform: "LinkedIn",
    emoji: "💼",
    title: "LinkedIn · Settimana 1 — Post 'founder story'",
    where: "Pubblica dal tuo profilo LinkedIn personale di Alfio. Lunedì mattina alle 8:00 CET per massimo engagement B2B.",
    whereUrl: "https://www.linkedin.com/post/new/",
    body: `L'estate scorsa a Perugia, un B&B familiare che conosco ha perso 8 prenotazioni in una sola settimana.

Non per prezzo. Non per recensioni. Per silenzio.

Un ospite tedesco che scriveva alle 22:40. Una coppia russa alle 23:15. Una famiglia francese di domenica.
Il proprietario — una signora di 58 anni — rispondeva in italiano, male e in ritardo, dopo colazione del giorno dopo.

Quelle 8 famiglie sono andate altrove.

Mi sono messo a studiare il problema. Non c'era una soluzione italiana vera: solo chatbot americani costosi, PMS enterprise sovradimensionati, o bot "ad albero" che spaventano gli ospiti con risposte stupide.

Così ho costruito Otelya. 🇮🇹

Una receptionist AI che:
→ Risponde in 30 lingue native, 24/7
→ Mantiene il TUO tono (non quello generico "da AI")
→ Zero allucinazioni: risponde solo su ciò che hai scritto nel Contesto
→ Pubblica da sola su Facebook e Instagram
→ Costa €119/mese (non $300 come i competitor americani)

Oggi è gratis per 14 giorni, nessuna carta richiesta.

Se gestisci un hotel, un B&B o un agriturismo e perdi prenotazioni per silenzio notturno/domenicale, scrivimi un messaggio qui su LinkedIn o a hello@otelya.app

👉 otelya.app

#ospitalitaitaliana #hospitalityai #hotelmanagement #startupitaliane #bedandbreakfast`,
  },
  {
    id: "linkedin-week-2",
    platform: "LinkedIn",
    emoji: "💼",
    title: "LinkedIn · Settimana 2 — Post 'risultato concreto' (adatta i numeri)",
    where: "Mercoledì ore 13:00 CET. Allega uno screenshot del Dashboard Analytics (sfoca dati personali).",
    whereUrl: "https://www.linkedin.com/post/new/",
    body: `Ieri notte Otelya ha gestito 23 messaggi in 7 lingue mentre dormivo.

Screenshot del Dashboard questa mattina (vedi sotto).

→ 14 WhatsApp in italiano, tedesco, russo
→ 6 email su Booking.com in inglese e francese  
→ 3 DM Instagram in spagnolo

Risposte generate in 30 secondi di media. Tempo mio stamani per gestire manualmente ciò che è rimasto: 11 minuti totali.

Un anno fa quello stesso volume mi sarebbe costato 2 ore di WhatsApp + 1 ora di email, in un italiano scolastico e in orari sbagliati.

Non è magia. È un prodotto costruito con un'ossessione specifica: che il tono della tua struttura passi anche attraverso l'AI.

Se sei un albergatore italiano che vuole smettere di perdere prenotazioni notturne per mancanza di risposta, Otelya è gratis per 14 giorni: otelya.app

Alfio

#hospitalityai #hotelmanagement #ospitalitaitaliana #startup #bedandbreakfast #agriturismo`,
  },
  {
    id: "linkedin-week-3",
    platform: "LinkedIn",
    emoji: "💼",
    title: "LinkedIn · Settimana 3 — Post 'contrarian/opinione'",
    where: "Venerdì ore 16:00 CET. Testo lungo = reach alto. Includi CTA a un articolo tuo o alla landing /compare.",
    whereUrl: "https://www.linkedin.com/post/new/",
    body: `Opinione impopolare: la maggior parte dei chatbot per hotel in Italia fa più male che bene.

Non parlo di Otelya (il mio). Parlo del settore in generale.

Problema #1: suonano "da AI".
"Grazie per averci contattati, siamo lieti di assisterti nella pianificazione del tuo soggiorno..."
Nessun ospite italiano scrive così. Nessun albergatore italiano risponde così.

Problema #2: inventano cose.
Un chatbot cliente stroncato pochi mesi fa diceva ai tedeschi "il parcheggio è GRATIS" quando era a 15€. Risultato: 3 recensioni Google 1-stella in una settimana.

Problema #3: costano come enterprise senza essere enterprise.
$300/mese per avere risposte ad albero che si confondono alla seconda domanda. Un B&B di 6 camere non può pagare questo.

La soluzione che ho costruito con Otelya:
1. Tono CUSTOM (non "da AI")  →  Voice Preset Wizard
2. Zero allucinazioni  →  l'AI risponde solo su ciò che hai scritto nel Contesto, altrimenti escala a te
3. Prezzo da €119/mese  →  che corrisponde al TUO costo reale, non a quello enterprise USA

Se gestisci una struttura italiana e sei scettico sull'AI dopo brutte esperienze, ti capisco. Ma guarda come lavora Otelya prima di scartare: otelya.app

#hospitality #hotels #AI #italia #startup`,
  },
  // -------- Reddit --------
  {
    id: "reddit-italyhotels",
    platform: "Reddit",
    emoji: "🔺",
    title: "Reddit · r/italyhotels — case study trasparente",
    where: "r/italyhotels (se non esiste, prova r/italy, r/hotels, r/entrepreneur, r/SideProject). Pubblica di sabato mattina. Niente link nel titolo, metti il link nel body.",
    whereUrl: "https://www.reddit.com/r/italy/",
    body: `**Ho costruito una receptionist AI per hotel italiani perché nessun prodotto esistente funzionava bene per noi — ecco cosa ho imparato in 6 mesi**

Mi chiamo Alfio, sono di Perugia. L'anno scorso ho visto una signora di 58 anni gestire un B&B familiare in Umbria perdere 8 prenotazioni in una settimana per non riuscire a rispondere di notte a ospiti tedeschi.

Ho fatto una lista dei chatbot per hotel disponibili in Italia nel 2026. Tutti presentavano uno di questi 3 problemi:

1. **Suonavano "da AI"**: risposte formali, piatte, nessuna personalità.
2. **Inventavano informazioni**: "il parcheggio è gratis" quando era a 15€.
3. **Costavano come enterprise americano**: $200-500/mese per un B&B di 6 camere.

Ho scritto Otelya in 4 mesi. Features principali:
- Risposte in 30 lingue native con il TONO della tua struttura (non generico)
- Zero allucinazioni by design: risponde solo su ciò che hai scritto nel Contesto
- Social Autopilot (Facebook + Instagram) incluso
- Prezzo trasparente: €119-199/mese
- Prova 14 giorni senza carta

Numeri (sincerità totale):
- 97 articoli blog già pubblicati
- Primo cliente pagante: non ancora. Siamo in fase pre-revenue
- Beta tester attivi: 3 strutture Umbre
- Costo mio sviluppo: 1.800€ in LLM credits + 400h del mio tempo

**Non è un post di marketing**. Sto cercando feedback onesto da chi gestisce strutture ricettive italiane: cosa manca al prodotto? Cosa vi irrita negli attuali chatbot? Quale sarebbe il prezzo massimo che pagherete oggi?

Link al sito se qualcuno vuole provare: otelya.app

Risponderò a tutti i commenti personalmente.`,
  },
  // -------- Facebook groups --------
  {
    id: "fb-group-albergatori",
    platform: "Facebook Groups",
    emoji: "👥",
    title: "Facebook Groups · Risposta utile ad altri (NO pitch)",
    where: "Entra in questi 3 gruppi: 'Albergatori Italiani', 'Proprietari di B&B Italia', 'Agriturismi Italia'. RISPONDI alle domande altrui per 2 settimane prima di menzionare Otelya. Mai postare pitch diretto.",
    whereUrl: "https://www.facebook.com/groups/search/groups/?q=albergatori%20italiani",
    body: `[QUESTO È UN TEMPLATE DI RISPOSTA — personalizza in base alla domanda]

Quando qualcuno nel gruppo chiede "come gestite i messaggi in alta stagione?" o "come rispondete ai tedeschi/russi?" o "cosa consigliate per Booking.com inbox?":

---

Ciao [Nome],

ti condivido quello che sta funzionando per me (gestisco una piccola struttura in Umbria).

Il problema di cui parli l'ho vissuto per 2 anni. Ho provato: segreteria telefonica, risposte template Booking, volunteer con l'inglese. Niente funzionava davvero.

Da 3 mesi uso una receptionist AI che si chiama Otelya (disclaimer: l'ho sviluppata io perché non trovavo nulla di adatto a strutture italiane piccole). Risponde ai messaggi in 30 lingue mantenendo il mio tono — non quello generico "da AI".

Se vuoi provarla c'è una prova gratuita di 14 giorni senza carta: otelya.app

In alternativa, se stai cercando una soluzione senza AI, un altro trucco che mi funziona è [consiglio pratico non-pitch]. 

Spero sia utile!

Alfio`,
  },
  // -------- Email outreach a associazioni --------
  {
    id: "email-federalberghi",
    platform: "Email · Federalberghi",
    emoji: "📧",
    title: "Email · Federalberghi (guest column proposal)",
    where: "Il nostro sistema li invia automaticamente da alfio@otelya.app con tag Resend cold-outreach-assoc.",
    whereUrl: "mailto:ufficiostampa@federalberghi.it?subject=Proposta%20guest%20column%3A%20AI%20nell%27ospitalit%C3%A0%20italiana",
    body: `Oggetto: Proposta guest column: AI nell'ospitalità italiana (prospettiva fondatore)

Buongiorno,

mi chiamo Alfio Moretti, fondatore di Otelya (otelya.app) — una SaaS italiana che offre receptionist AI multilingua a hotel, B&B e agriturismi indipendenti.

Vi scrivo per proporre un articolo guest per il vostro blog/newsletter su un tema che molti vostri associati stanno affrontando con confusione: come l'intelligenza artificiale sta cambiando la relazione tra struttura ricettiva e ospite.

Posso proporre uno di questi 3 angoli:

1. **"3 errori che gli albergatori italiani fanno quando introducono l'AI (e come evitarli)"** — case study da aziende reali che hanno sbagliato approccio
2. **"L'ospitalità italiana è un valore. L'AI può preservarlo?"** — riflessione culturale, non tecnica
3. **"Guida pratica: le 5 domande da fare prima di adottare un chatbot per il tuo hotel"** — pratico, utile, no marketing

Non chiedo compensi. Chiedo solo: linkback a otelya.app a fondo articolo + possibilità di menzionare il mio prodotto in modo contestuale (non intrusivo).

Posso consegnare l'articolo in 4-5 giorni, ~1200 parole, con fonti verificate.

Grazie dell'attenzione.

Alfio Moretti
Fondatore, Otelya
+39 349 639 5904
alfio@otelya.app
otelya.app`,
  },
  {
    id: "email-confturismo",
    platform: "Email · Confturismo",
    emoji: "📧",
    title: "Email · Confturismo (webinar/evento proposal)",
    where: "Invia a: info@confturismo.it + segreteria@confturismo.it. Modifica il tema in base a loro agenda 2026.",
    whereUrl: "mailto:info@confturismo.it?subject=Proposta%20webinar%3A%20AI%20per%20le%20piccole%20strutture%20ricettive",
    body: `Oggetto: Proposta webinar gratuito: AI per le piccole strutture ricettive

Buongiorno,

mi chiamo Alfio Moretti, fondatore di Otelya (otelya.app).

Ho notato che Confturismo organizza eventi formativi per i propri associati sul tema digitalizzazione. Vorrei proporvi un webinar gratuito di 45 minuti su un argomento specifico e pratico:

**"AI pratica per il piccolo albergatore italiano: 7 cose che puoi automatizzare oggi senza perdere il tuo stile."**

Il webinar sarà:
- 100% gratuito per i vostri associati
- Zero vendita diretta (solo contenuto)
- Fondato su casi reali italiani (non slide di marketing)
- Con 10 minuti di Q&A finale

Come fondatore sarei il relatore principale. Fornirei anche: slides, replay video, checklist PDF scaricabile per i vostri associati.

In cambio chiedo: 
1) Promozione del webinar sui vostri canali
2) Menzione di Otelya come "il mio prodotto, se qualcuno vuole saperne di più"
3) Lista degli iscritti al webinar (con loro consenso GDPR) per follow-up individuale

Date libere per me: lunedì-venerdì dalle 15:00 alle 18:00.

Grazie dell'attenzione.

Alfio Moretti
Fondatore, Otelya
+39 349 639 5904
alfio@otelya.app`,
  },
  {
    id: "email-agriturist",
    platform: "Email · Agriturist",
    emoji: "📧",
    title: "Email · Agriturist (partnership gratuita primi 10)",
    where: "Invia a: info@agriturist.it. Agriturist è il canale perfetto per il tuo ICP (agriturismi Umbria/Toscana).",
    whereUrl: "mailto:info@agriturist.it?subject=Partnership%20gratuita%3A%2010%20agriturismi%20pilota%20Otelya",
    body: `Oggetto: Partnership gratuita: 10 agriturismi pilota Otelya

Gentile Agriturist,

mi chiamo Alfio Moretti, sono di Perugia e ho fondato Otelya (otelya.app), una receptionist AI multilingua pensata esattamente per gli agriturismi italiani indipendenti.

Vi scrivo con una proposta concreta: offrire **3 mesi gratuiti del piano Annuale** (valore €357) ai primi 10 agriturismi vostri associati che vorrà provare Otelya.

Perché?
1. Il mio ICP è l'agriturismo italiano 4-20 unità. Il canale associativo è quello giusto.
2. Ho bisogno di 10 testimonianze video reali per lanciare il prodotto nel mercato più ampio.
3. Gli agriturismi che proveranno diventeranno — spero — clienti paganti dal 4° mese.

Cosa offrirei agli agriturismi:
- 3 mesi gratis (nessuna carta richiesta)
- Setup personalizzato gratuito (con me direttamente)
- Priorità supporto 24/7 durante la prova
- Nessun impegno a continuare

In cambio chiedo:
- A fine prova: 1 testimonianza video (90 secondi, girata con smartphone)
- 1 referral obbligatorio (presentarmi un altro agriturismo)

Cosa chiedo ad Agriturist:
- Email ai vostri associati (massimo 1) con questa proposta
- Possibilità di menzionare la partnership sul vostro sito

Nessun pagamento richiesto da nessuna parte. Puro vincere/vincere.

Grazie dell'attenzione.

Alfio Moretti
Fondatore, Otelya
+39 349 639 5904
alfio@otelya.app`,
  },
  // -------- Podcast pitch --------
  {
    id: "podcast-pitch",
    platform: "Email · Podcast italiani hospitality",
    emoji: "🎙️",
    title: "Email · Pitch per podcast hospitality italiani",
    where: "Invia a 3 podcast: Rivoluzione Albergatore (rivoluzionealbergatore.com), Hotel in Fermento (hotelinfermento.it), Travel Innovation Lab. Trova le email dai siti ufficiali.",
    whereUrl: "https://www.google.com/search?q=rivoluzione+albergatore+podcast+contatti",
    body: `Oggetto: Proposta ospite: la storia di un software italiano fatto per sostituire chi non voleva essere sostituito

Ciao [Nome],

seguo il tuo podcast da [X mesi] e mi complimento per la qualità delle conversazioni che riesci a far emergere con gli ospiti.

Mi chiamo Alfio Moretti e ho fondato Otelya (otelya.app), una receptionist AI multilingua per strutture ricettive italiane.

Vorrei propormi come ospite per un episodio futuro. La storia che posso raccontare — in modo autentico, non promozionale — tocca alcuni dei temi più caldi del tuo podcast:

1. **Il paradosso dell'AI nell'ospitalità italiana**: come inserire tecnologia in un settore che vende "calore umano"
2. **Il mio errore iniziale**: pensare che gli albergatori italiani fossero contrari all'AI (sbagliato — sono contrari ai chatbot stupidi)
3. **Perché ho rifiutato $50k di investimento** per restare bootstrap e non snaturare il prodotto
4. **Il futuro realistico**: dove si fermerà l'AI nell'ospitalità italiana (secondo me)

Posso raccontare numeri concreti (fase pre-revenue, metriche prodotto, strategia 2026), aneddoti dalla beta (incluso un epico fallimento), e lezioni che credo possano servire ad altri founder italiani.

Durata ideale per me: 45-60 minuti.
Formato: in presenza se sei nel centro Italia, altrimenti remoto.

Se interessa, rispondi anche con due righe: parliamo prima di fissare.

Grazie,

Alfio Moretti
+39 [TUO NUMERO]
alfiomoretti@icloud.com
otelya.app`,
  },
  // -------- Influencer collab --------
  {
    id: "influencer-collab",
    platform: "DM Instagram · Micro-influencer travel",
    emoji: "📸",
    title: "DM Instagram · Collab con micro-influencer travel (10-50k follower)",
    where: "Cerca su Instagram hashtag #travelitaly #hotelsitaly #boutiqueitaly #italybookings. Scrivi DM solo a profili tra 10k e 50k follower (sotto 10k = micro-reach, sopra 50k = chiedono soldi).",
    whereUrl: "https://www.instagram.com/explore/tags/travelitaly/",
    body: `Ciao [Nome]!

Alfio di Otelya — ti seguo da un po', apprezzo come racconti [specifica tipo di contenuto che fa, es: boutique hotels umbri, agriturismi toscani, piccole esperienze autentiche].

Ti scrivo per una collab semplice e non invasiva:

Ho costruito Otelya (otelya.app) — una receptionist AI pensata per piccole strutture italiane indipendenti. Non la conoscete ancora quasi nessuno perché è nata nel 2026.

Ti propongo:
1. **Accesso Otelya gratis a vita** per te personalmente (se mai aprirai una struttura o se vuoi testarla su una struttura amica)
2. **Un piano annuale GRATIS (valore €1.428) per una struttura di tua scelta** (una delle tue amiche albergatrice, o una che vorresti supportare)

In cambio:
- 1 Reel di 30-45 secondi sul tuo profilo in cui mostri Otelya in azione (ti preparo io il brief + il video 9:16 che puoi usare come base)
- 1 story con mention @otelya.hq
- Nessun obbligo di dire "sponsored" se preferisci — ma per trasparenza con il tuo pubblico mettere "collaborazione" nei primi secondi del Reel sarebbe più onesto

Niente contratto formale. Vincere/vincere.

Interessa?

Alfio
alfiomoretti@icloud.com`,
  },
  // -------- Agency partnership --------
  {
    id: "agency-partnership",
    platform: "DM LinkedIn · Agenzie consulenza alberghiera",
    emoji: "🤝",
    title: "LinkedIn · Partner con agenzie di consulenza alberghiera (rev-share 20%)",
    where: "Cerca su LinkedIn 'consulente alberghiero Italia', 'revenue manager hotel freelance', 'consulting hospitality Italia'. Scrivi DM a 20-30 profili.",
    whereUrl: "https://www.linkedin.com/search/results/people/?keywords=consulente%20alberghiero%20italia",
    body: `Ciao [Nome],

Alfio Moretti qui — fondatore di Otelya (otelya.app), SaaS italiana per receptionist AI multilingua su hotel/B&B/agriturismi indipendenti.

Ti scrivo per una proposta di partnership diretta, senza giri di parole.

**Proposta rev-share 20% a vita** per ogni cliente che porti a Otelya.

Numeri concreti:
- Piano annuale Otelya: €1.428/anno per il cliente
- La tua quota: €285/anno a vita, finché il cliente resta attivo
- 10 clienti = €2.850/anno di passive income per te

Perché ha senso:
- Il tuo cliente in gestione ricettiva ha probabilmente già il problema "non riesco a rispondere di notte in lingue straniere" — Otelya lo risolve a €119/mese, molto meno di assumere personale
- Tu non vendi Otelya: proponi una soluzione a un problema reale
- Zero investimento tuo. Zero tempo di implementazione (Otelya si setuppa da sola)

Modello:
1. Ti mando un link di referral unico (es: otelya.app?r=tuoid)
2. Chi arriva da quel link e paga, tu guadagni 20% a vita
3. Dashboard analytics con tracking in tempo reale (lo stiamo rilasciando)
4. Pagamenti mensili via bonifico (nessuna soglia minima)

Se ti interessa: rispondi con due righe e organizziamo una call di 20 minuti per capire se match.

Alfio
alfiomoretti@icloud.com
otelya.app`,
  },
];

export default function GrowthKit() {
  const [copied, setCopied] = useState(null);

  const copyText = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Testo copiato negli appunti");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6" data-testid="growth-kit-page">
      <div>
        <h2 className="font-serif text-3xl text-ink">Growth Kit</h2>
        <p className="mt-2 text-sm text-stone max-w-3xl">
          Testi pronti da copiare e incollare per far crescere Otelya senza budget. Ogni card ha: il testo (già scritto nel tono di Alfio), il bottone copia e le istruzioni esatte su dove pubblicare. Non pubblicare dagli account aziendali — pubblica dai tuoi account personali.
        </p>
      </div>

      <div className="grid gap-4">
        {DRAFTS.map((d) => (
          <div key={d.id} className="rounded-2xl border border-line bg-canvas p-6" data-testid={`growth-card-${d.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl mt-0.5">{d.emoji}</div>
                <div>
                  <div className="text-xs text-stone uppercase tracking-wide">{d.platform}</div>
                  <h3 className="font-serif text-xl text-ink mt-1">{d.title}</h3>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a href={d.whereUrl} target="_blank" rel="noreferrer">
                  <Button variant="ghost" className="rounded-full h-8 text-xs" data-testid={`growth-open-${d.id}`}>
                    <ArrowSquareOut size={14} className="mr-1" /> Apri
                  </Button>
                </a>
                <Button onClick={() => copyText(d.id, d.body)} className="rounded-full h-8 bg-ink text-canvas text-xs hover:bg-olive" data-testid={`growth-copy-${d.id}`}>
                  <Copy size={14} className="mr-1" /> {copied === d.id ? "Copiato ✓" : "Copia testo"}
                </Button>
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-sand/40 border border-line/50 p-3 text-xs text-stone">
              <strong>Dove pubblicare:</strong> {d.where}
            </div>

            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-terracotta hover:underline">Vedi testo completo ↓</summary>
              <pre className="mt-3 whitespace-pre-wrap text-sm text-ink font-sans bg-sand/20 rounded-lg p-4 border border-line/40 max-h-[500px] overflow-y-auto">{d.body}</pre>
            </details>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-ink text-canvas p-6 mt-8">
        <h3 className="font-serif text-xl">Perché non pubblico io direttamente dai tuoi account?</h3>
        <p className="mt-2 text-sm opacity-80 leading-relaxed">
          Pubblicare automaticamente dal tuo profilo LinkedIn, Reddit o dai gruppi Facebook violerebbe i termini di servizio di quelle piattaforme e rischierebbe di far sospendere i tuoi account. Il mio ruolo è farti arrivare al 90% del lavoro: ho scritto ogni testo nel tuo tono, ho trovato i canali giusti, ho indicato date e orari ottimali. Tu metti il 10% finale: copia, incolla, pubblica.
        </p>
        <p className="mt-3 text-sm opacity-80">
          <strong>Email outreach a Federalberghi/Confturismo/Agriturist</strong>: queste 3 posso aggiungerle come campagne nella Cold Outreach pipeline già attiva se me lo chiedi — in quel caso le invio io automaticamente dalle caselle outreach con tracking lettura.
        </p>
      </div>
    </div>
  );
}
